import moment from 'moment-timezone';
import { IROPS_STATUS } from '@app/enums/irops.enum';

type Moment = moment.Moment;

/**
 * Ventana operativa de documentación para vuelos con afectaciones (SSBD).
 *
 * Helpers puros y centralizados (sin Router, sin servicios) que resuelven la
 * hora efectiva de salida, el estado de la ventana, la clasificación MIN/MAJ y
 * la elegibilidad de documentación por estatus de segmento.
 *
 * Zona horaria (contrato real): `departureDateTime` (STD) y
 * `estimatedDepartureTime` (ETD) llegan como wall-clock local de la estación de
 * salida, sin `Z` ni offset (ej. "2026-07-23T18:55:00"). La zona IANA viaja en
 * `departureAirportTimeZoneId` (ej. "America/Mexico_City"). Estas fechas naive
 * se interpretan DENTRO de esa zona con moment-timezone (`moment.tz`), nunca
 * con `new Date(string)` / `moment(string)` para luego cambiar de zona (eso las
 * ataría primero a la zona del dispositivo). Si el valor excepcionalmente trae
 * `Z` u offset se trata como instante absoluto. Si la zona falta, está vacía o
 * es inválida, se cae a la zona local del kiosko (ver `resolveTimeZone`).
 */

/** Horas antes de la salida efectiva en que abre la ventana de documentación. */
export const DOCUMENTATION_WINDOW_OPEN_HOURS = 4;
/** Minutos antes de la salida efectiva en que cierra la ventana (todas las estaciones). */
export const DOCUMENTATION_WINDOW_CLOSE_MINUTES = 60;

/** Umbrales de clasificación de afectación (minutos). */
export const AFFECTATION_DELAY_MAJOR_MINUTES = 30;
export const AFFECTATION_EARLY_MAJOR_MINUTES = 15;

/** Estado de la ventana operativa de documentación. */
export type DocumentationWindowState = 'BEFORE_OPEN' | 'OPEN' | 'BOARDING' | 'CLOSED' | 'INVALID';

/** Clasificación de una afectación. No bloquea documentación por sí sola. */
export type AffectationClassification = 'MIN' | 'MAJ';

/** Origen de la hora efectiva resuelta. */
export type EffectiveDepartureSource = 'ETD' | 'STD';

/** Decisión de elegibilidad de documentación para el segmento actual. */
export type DocumentationEligibility =
  | 'ALLOW'
  | 'BLOCK_CANCELLED'
  | 'BLOCK_UNCONFIRMED'
  | 'BLOCK_BEFORE_OPEN'
  | 'BOARDING'
  | 'CLOSED'
  | 'BLOCK_INVALID'
  | 'PRESERVE_EXISTING';

/** Forma mínima de segmento consumida por los helpers (campos reales del contrato). */
export interface DocumentationSegmentLike {
  departureDateTime?: string;
  estimatedDepartureTime?: string;
  segmentStatusCode?: string;
  departureAirportTimeZoneId?: string;
  flightsNewInfo?: {
    status?: string;
    estimatedDepartureTime?: string;
    originalDepartureTime?: string;
    departureDateTime?: string;
    departureDifferenceInMinutes?: number;
  } | null;
}

/** Estatus de confirmación que bloquean SSBD por itinerario no confirmado. */
const UNCONFIRMED_SEGMENT_STATUS = ['SC', 'WK'];
const CONFIRMED_SEGMENT_STATUS = 'HK';

/**
 * Detecta si una fecha ISO trae zona explícita (`Z` u offset `±HH:MM`) al final.
 * En ese caso debe tratarse como instante absoluto y NO reinterpretarse en la
 * zona de la estación.
 */
function hasExplicitOffset(value: string): boolean {
  return /(?:Z|[+-]\d{2}:?\d{2})$/.test(value.trim());
}

/** Valida que una cadena represente una fecha/hora parseable. */
function isValidDateTime(value: string | undefined | null): value is string {
  return !!value && moment(value, moment.ISO_8601, true).isValid();
}

/**
 * Valida que `timeZoneId` sea una zona IANA reconocida por la base de datos de
 * moment-timezone. Centraliza la validación (§5) para todos los consumidores.
 */
export function isValidTimeZone(timeZoneId: string | undefined | null): timeZoneId is string {
  return !!timeZoneId && !!moment.tz.zone(timeZoneId);
}

/**
 * Zona horaria efectiva: la de la estación (`departureAirportTimeZoneId`) si es
 * válida; en caso contrario la zona local del kiosko/dispositivo como respaldo
 * seguro (§5). Nunca lanza excepción ni inventa una zona fija.
 */
export function resolveTimeZone(
  timeZoneId: string | undefined | null,
  fallbackTimeZone: string = moment.tz.guess()
): string {
  if (isValidTimeZone(timeZoneId)) {
    return timeZoneId;
  }
  return isValidTimeZone(fallbackTimeZone) ? fallbackTimeZone : moment.tz.guess();
}

/**
 * Parsea una fecha del contrato como instante absoluto, interpretándola dentro
 * de la zona de la estación cuando es local-naive.
 *
 * - Con `Z`/offset → instante absoluto (`moment(value)`).
 * - Local-naive    → `moment.tz(value, zona)` (interpretada en la estación).
 *
 * Devuelve `null` si la fecha no es válida (no inventa una hora).
 */
export function parseStationDateTime(
  value: string | undefined | null,
  timeZoneId?: string | null
): Moment | null {
  if (!isValidDateTime(value)) {
    return null;
  }
  if (hasExplicitOffset(value)) {
    return moment(value);
  }
  return moment.tz(value, resolveTimeZone(timeZoneId));
}

/**
 * Hora efectiva de salida: ETD ?? STD.
 * ETD = `estimatedDepartureTime` (de flightsNewInfo o del segmento).
 * STD = `departureDateTime` (horario programado).
 * Devuelve `null` cuando no existe ninguna fecha válida (no inventa una hora).
 */
export function resolveEffectiveDepartureTime(
  segment: DocumentationSegmentLike | null | undefined
): { value: string; source: EffectiveDepartureSource } | null {
  // Se evalúa cada candidato de ETD por validez (no con `??`): un ETD presente
  // pero inválido (p. ej. "invalid-date") NO debe descartar el siguiente
  // candidato ni forzar INVALID cuando el STD sí es válido.
  const etdCandidates = [
    segment?.flightsNewInfo?.estimatedDepartureTime,
    segment?.estimatedDepartureTime,
  ];
  const etd = etdCandidates.find((candidate) => isValidDateTime(candidate));
  if (isValidDateTime(etd)) {
    return { value: etd, source: 'ETD' };
  }

  const std = segment?.departureDateTime;
  if (isValidDateTime(std)) {
    return { value: std, source: 'STD' };
  }

  return null;
}

/**
 * Estado de la ventana operativa según la hora efectiva y `now`.
 *
 * openTime  = effectiveDeparture - 4h
 * closeTime = effectiveDeparture - 60m
 *
 * now < open              → BEFORE_OPEN
 * open <= now < close     → OPEN
 * close <= now < depart   → BOARDING
 * now >= depart           → CLOSED
 * sin hora válida         → INVALID
 *
 * `effectiveDeparture` es wall-clock local de la estación; se interpreta dentro
 * de `timeZoneId` (fallback local si es inválida). `now` es un instante
 * absoluto (por defecto la hora del dispositivo).
 */
export function resolveDocumentationWindowState(
  effectiveDeparture: string | null | undefined,
  now: Moment | Date | string = moment(),
  timeZoneId?: string | null
): DocumentationWindowState {
  const departure = parseStationDateTime(effectiveDeparture, timeZoneId);
  if (!departure) {
    return 'INVALID';
  }

  const nowMoment = moment(now);
  if (!nowMoment.isValid()) {
    return 'INVALID';
  }

  const openTime = departure.clone().subtract(DOCUMENTATION_WINDOW_OPEN_HOURS, 'hours');
  const closeTime = departure.clone().subtract(DOCUMENTATION_WINDOW_CLOSE_MINUTES, 'minutes');

  if (nowMoment.isBefore(openTime)) {
    return 'BEFORE_OPEN';
  }
  if (nowMoment.isBefore(closeTime)) {
    return 'OPEN';
  }
  if (nowMoment.isBefore(departure)) {
    return 'BOARDING';
  }
  return 'CLOSED';
}

/**
 * Minutos que faltan para que abra la ventana (BEFORE_OPEN). Se calcula contra
 * openTime = effectiveDeparture - 4h. Devuelve `null` si ya abrió o no hay hora
 * válida, para evitar mostrar "Disponible en 0:00" fuera de BEFORE_OPEN.
 */
export function minutesUntilWindowOpens(
  effectiveDeparture: string | null | undefined,
  now: Moment | Date | string = moment(),
  timeZoneId?: string | null
): number | null {
  const departure = parseStationDateTime(effectiveDeparture, timeZoneId);
  if (!departure) {
    return null;
  }
  const nowMoment = moment(now);
  if (!nowMoment.isValid()) {
    return null;
  }

  const openTime = departure.clone().subtract(DOCUMENTATION_WINDOW_OPEN_HOURS, 'hours');
  if (!nowMoment.isBefore(openTime)) {
    return null;
  }
  return openTime.diff(nowMoment, 'minutes');
}

/**
 * Clasificación MIN/MAJ de la afectación. NO debe usarse por sí sola para
 * bloquear documentación de un segmento HK.
 * Demora: <30 MIN, >=30 MAJ. Adelanto: |min|<15 MIN, >=15 MAJ. Cancelado: MAJ.
 * Devuelve `null` cuando no hay afectación clasificable o faltan minutos.
 */
export function classifyAffectation(
  status: string | undefined,
  minutes: number | null | undefined
): AffectationClassification | null {
  if (status === IROPS_STATUS.CANCELLED) {
    return 'MAJ';
  }

  if (typeof minutes !== 'number' || !Number.isFinite(minutes)) {
    return null;
  }
  const absMinutes = Math.abs(minutes);

  if (status === IROPS_STATUS.DELAYED) {
    return absMinutes >= AFFECTATION_DELAY_MAJOR_MINUTES ? 'MAJ' : 'MIN';
  }
  if (status === IROPS_STATUS.EARLY) {
    return absMinutes >= AFFECTATION_EARLY_MAJOR_MINUTES ? 'MAJ' : 'MIN';
  }

  return null;
}

/**
 * Elegibilidad de documentación del segmento actual según estatus operativo,
 * estatus de confirmación (HK/SC/WK) y ventana. La clasificación MIN/MAJ NO
 * participa: un HK dentro de ventana documenta aunque sea MIN o MAJ.
 *
 * - Cancelled (estatus operativo)      → BLOCK_CANCELLED (siempre).
 * - segmentStatusCode SC/WK            → BLOCK_UNCONFIRMED.
 * - HK → según ventana: OPEN→ALLOW, BEFORE_OPEN→BLOCK_BEFORE_OPEN,
 *        BOARDING→BOARDING, CLOSED→CLOSED, INVALID→BLOCK_INVALID.
 * - otro estatus de confirmación       → PRESERVE_EXISTING.
 */
export function resolveDocumentationEligibility(params: {
  segmentStatusCode: string | undefined;
  flightStatus: string | undefined;
  windowState: DocumentationWindowState;
}): DocumentationEligibility {
  const { segmentStatusCode, flightStatus, windowState } = params;

  if (flightStatus === IROPS_STATUS.CANCELLED) {
    return 'BLOCK_CANCELLED';
  }

  const normalizedStatusCode = (segmentStatusCode || '').toUpperCase();

  if (UNCONFIRMED_SEGMENT_STATUS.some((code) => normalizedStatusCode.includes(code))) {
    return 'BLOCK_UNCONFIRMED';
  }

  if (normalizedStatusCode.includes(CONFIRMED_SEGMENT_STATUS)) {
    switch (windowState) {
      case 'OPEN':
        return 'ALLOW';
      case 'BEFORE_OPEN':
        return 'BLOCK_BEFORE_OPEN';
      case 'BOARDING':
        return 'BOARDING';
      case 'CLOSED':
        return 'CLOSED';
      case 'INVALID':
      default:
        return 'BLOCK_INVALID';
    }
  }

  return 'PRESERVE_EXISTING';
}

/** Resultado centralizado de la evaluación de un segmento (§13/§15). */
export interface SegmentDocumentationResult {
  effectiveDeparture: string | null;
  effectiveSource: EffectiveDepartureSource | null;
  timeZone: string;
  windowState: DocumentationWindowState;
  minutesUntilOpen: number | null;
  eligibility: DocumentationEligibility;
}

/**
 * Evaluación de extremo a extremo del `currentSegment`: resuelve hora efectiva,
 * zona (con fallback), estado de ventana, minutos para apertura y elegibilidad.
 * Punto único que consumen servicios/componentes; mantiene toda la lógica pura
 * y testeable, sin Router ni efectos.
 */
export function evaluateSegmentDocumentation(
  segment: DocumentationSegmentLike | null | undefined,
  now: Moment | Date | string = moment()
): SegmentDocumentationResult {
  const timeZone = resolveTimeZone(segment?.departureAirportTimeZoneId);
  const effective = resolveEffectiveDepartureTime(segment);
  const effectiveDeparture = effective?.value ?? null;
  const windowState = resolveDocumentationWindowState(effectiveDeparture, now, timeZone);
  const minutesUntilOpen = minutesUntilWindowOpens(effectiveDeparture, now, timeZone);
  const eligibility = resolveDocumentationEligibility({
    segmentStatusCode: segment?.segmentStatusCode,
    flightStatus: segment?.flightsNewInfo?.status,
    windowState,
  });

  return {
    effectiveDeparture,
    effectiveSource: effective?.source ?? null,
    timeZone,
    windowState,
    minutesUntilOpen,
    eligibility,
  };
}
